import { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { 
  ArrowLeft, Calendar, Eye, Clock, Tag, Share2, 
  BookOpen, FileText, Video, Download, Play,
  Facebook, Twitter, Linkedin, Copy, Check,
  MessageCircle, Send, Trash2, Pencil, User, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BlogPost = () => {
  const { slugOrId } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  
  // Comments state
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [editingComment, setEditingComment] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    fetchPost();
    checkAuth();
  }, [slugOrId]);

  const checkAuth = () => {
    const token = localStorage.getItem('token');
    const user = localStorage.getItem('user');
    if (token && user) {
      setIsLoggedIn(true);
      try {
        setCurrentUser(JSON.parse(user));
      } catch {
        setCurrentUser(null);
      }
    }
  };

  const fetchPost = async () => {
    try {
      // Try by slug first, then by ID
      let response;
      try {
        response = await axios.get(`${API}/blog/slug/${slugOrId}`);
      } catch {
        response = await axios.get(`${API}/blog/${slugOrId}`);
      }
      setPost(response.data);
      fetchComments(response.data.id);
      
      // Fetch related posts in same category
      if (response.data.category) {
        const relatedRes = await axios.get(`${API}/blog?category=${response.data.category}&limit=4`);
        setRelatedPosts(
          (relatedRes.data.posts || []).filter(p => p.id !== response.data.id).slice(0, 3)
        );
      }
    } catch (error) {
      console.error('Failed to fetch post:', error);
      toast.error('Article not found');
      navigate('/blog');
    } finally {
      setLoading(false);
    }
  };

  const fetchComments = async (postId) => {
    try {
      const response = await axios.get(`${API}/blog/${postId}/comments`);
      setComments(response.data);
    } catch (error) {
      console.error('Failed to fetch comments:', error);
    }
  };

  const handleSubmitComment = async (e) => {
    e.preventDefault();
    if (!newComment.trim()) return;
    
    if (!isLoggedIn) {
      toast.error('Please log in to comment');
      navigate('/login');
      return;
    }

    setSubmittingComment(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${API}/blog/${post.id}/comments`,
        { content: newComment },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments([response.data, ...comments]);
      setNewComment('');
      toast.success('Comment posted!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to post comment';
      toast.error(msg);
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleEditComment = async (commentId) => {
    if (!editContent.trim()) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.put(
        `${API}/blog/comments/${commentId}`,
        { content: editContent },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setComments(comments.map(c => c.id === commentId ? response.data : c));
      setEditingComment(null);
      setEditContent('');
      toast.success('Comment updated!');
    } catch (error) {
      const msg = error.response?.data?.detail || 'Failed to update comment';
      toast.error(msg);
    }
  };

  const handleDeleteComment = async (commentId) => {
    if (!window.confirm('Delete this comment?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/blog/comments/${commentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setComments(comments.filter(c => c.id !== commentId));
      toast.success('Comment deleted');
    } catch (error) {
      toast.error('Failed to delete comment');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatCommentDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const getReadingTime = (content) => {
    const words = content?.split(/\s+/).length || 0;
    const minutes = Math.ceil(words / 200);
    return `${minutes} min read`;
  };

  const handleShare = (platform) => {
    const url = window.location.href;
    const title = post?.title || '';
    
    const shareUrls = {
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`,
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`,
    };
    
    if (shareUrls[platform]) {
      window.open(shareUrls[platform], '_blank', 'width=600,height=400');
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const renderContent = (content) => {
    let html = content
      .replace(/^### (.*$)/gim, '<h3 class="font-serif text-xl font-bold mt-8 mb-4">$1</h3>')
      .replace(/^## (.*$)/gim, '<h2 class="font-serif text-2xl font-bold mt-10 mb-4">$1</h2>')
      .replace(/^# (.*$)/gim, '<h1 class="font-serif text-3xl font-bold mt-12 mb-6">$1</h1>')
      .replace(/\*\*(.*?)\*\*/gim, '<strong>$1</strong>')
      .replace(/\*(.*?)\*/gim, '<em>$1</em>')
      .replace(/\n\n/gim, '</p><p class="mb-4 leading-relaxed">')
      .replace(/\n/gim, '<br />');
    
    return `<p class="mb-4 leading-relaxed">${html}</p>`;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!post) {
    return null;
  }

  return (
    <div className="min-h-screen bg-background" data-testid="blog-post-page">
      {/* Hero */}
      <section className="relative">
        {post.cover_image ? (
          <div className="relative h-[40vh] sm:h-[50vh] lg:h-[60vh] overflow-hidden">
            <img
              src={post.cover_image}
              alt={post.title}
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
          </div>
        ) : (
          <div className="h-32 sm:h-48 bg-gradient-to-br from-primary/10 to-accent/10" />
        )}
      </section>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 -mt-32 relative z-10 pb-16">
        {/* Back button */}
        <Link 
          to="/blog" 
          className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-6 transition-colors"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to Journal
        </Link>

        <Card className="overflow-hidden shadow-xl border-border/50">
          <CardContent className="p-6 sm:p-10 lg:p-12">
            {/* Meta */}
            <div className="flex flex-wrap items-center gap-3 mb-6">
              <Badge variant="secondary" className="font-normal">
                {post.category || 'General'}
              </Badge>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Calendar className="h-3.5 w-3.5" />
                {formatDate(post.published_at || post.created_at)}
              </span>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Clock className="h-3.5 w-3.5" />
                {getReadingTime(post.content)}
              </span>
              <span className="text-sm text-muted-foreground flex items-center gap-1">
                <Eye className="h-3.5 w-3.5" />
                {post.views || 0} views
              </span>
            </div>

            {/* Title */}
            <h1 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              {post.title}
            </h1>

            {/* Author */}
            <div className="flex items-center gap-3 pb-8 border-b mb-8">
              <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                <BookOpen className="h-5 w-5 text-primary" />
              </div>
              <div>
                <p className="font-medium text-foreground">{post.author_name || 'Brookzone Team'}</p>
                <p className="text-sm text-muted-foreground">Author</p>
              </div>
            </div>

            {/* Video embed */}
            {post.video_url && (
              <div className="mb-8 rounded-lg overflow-hidden bg-muted aspect-video">
                {post.video_url.includes('youtube') || post.video_url.includes('youtu.be') ? (
                  <iframe
                    src={post.video_url.replace('watch?v=', 'embed/').replace('youtu.be/', 'youtube.com/embed/')}
                    title="Video"
                    className="w-full h-full"
                    allowFullScreen
                  />
                ) : post.video_url.includes('vimeo') ? (
                  <iframe
                    src={post.video_url.replace('vimeo.com/', 'player.vimeo.com/video/')}
                    title="Video"
                    className="w-full h-full"
                    allowFullScreen
                  />
                ) : (
                  <video controls className="w-full h-full">
                    <source src={post.video_url} />
                  </video>
                )}
              </div>
            )}

            {/* Document download */}
            {post.document_url && (
              <div className="mb-8 p-4 bg-muted/50 rounded-lg border border-border/50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                      <FileText className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{post.document_name || 'Download Document'}</p>
                      <p className="text-sm text-muted-foreground">Attached document</p>
                    </div>
                  </div>
                  <a 
                    href={`${process.env.REACT_APP_BACKEND_URL}${post.document_url}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Download
                    </Button>
                  </a>
                </div>
              </div>
            )}

            {/* Content */}
            <div 
              className="prose prose-lg max-w-none text-foreground"
              dangerouslySetInnerHTML={{ __html: renderContent(post.content) }}
            />

            {/* Tags */}
            {post.tags && post.tags.length > 0 && (
              <div className="mt-10 pt-8 border-t">
                <div className="flex items-center gap-2 flex-wrap">
                  <Tag className="h-4 w-4 text-muted-foreground" />
                  {post.tags.map((tag) => (
                    <Badge key={tag} variant="outline" className="font-normal">
                      #{tag}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Share */}
            <div className="mt-8 pt-8 border-t">
              <p className="text-sm font-medium text-muted-foreground mb-4 flex items-center gap-2">
                <Share2 className="h-4 w-4" />
                Share this article
              </p>
              <div className="flex gap-2">
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleShare('facebook')}
                  className="hover:bg-blue-50 hover:text-blue-600 hover:border-blue-200"
                >
                  <Facebook className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleShare('twitter')}
                  className="hover:bg-sky-50 hover:text-sky-600 hover:border-sky-200"
                >
                  <Twitter className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="icon"
                  onClick={() => handleShare('linkedin')}
                  className="hover:bg-blue-50 hover:text-blue-700 hover:border-blue-200"
                >
                  <Linkedin className="h-4 w-4" />
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={handleCopyLink}
                  className="gap-2"
                >
                  {copied ? <Check className="h-4 w-4" /> : <Copy className="h-4 w-4" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Comments Section */}
        <section className="mt-12" data-testid="comments-section">
          <div className="flex items-center gap-2 mb-6">
            <MessageCircle className="h-5 w-5 text-primary" />
            <h2 className="font-serif text-2xl font-bold text-foreground">
              Comments ({comments.length})
            </h2>
          </div>

          {/* Comment Form */}
          <Card className="mb-8 border-border/50">
            <CardContent className="p-6">
              {isLoggedIn ? (
                <form onSubmit={handleSubmitComment}>
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <User className="h-5 w-5 text-primary" />
                    </div>
                    <div className="flex-1">
                      <Textarea
                        value={newComment}
                        onChange={(e) => setNewComment(e.target.value)}
                        placeholder="Share your thoughts..."
                        rows={3}
                        className="mb-3"
                        maxLength={2000}
                        data-testid="comment-input"
                      />
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-muted-foreground">
                          {newComment.length}/2000
                        </span>
                        <Button 
                          type="submit" 
                          disabled={!newComment.trim() || submittingComment}
                          className="btn-accent"
                          data-testid="submit-comment"
                        >
                          {submittingComment ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : (
                            <Send className="h-4 w-4 mr-2" />
                          )}
                          Post Comment
                        </Button>
                      </div>
                    </div>
                  </div>
                </form>
              ) : (
                <div className="text-center py-4">
                  <p className="text-muted-foreground mb-3">
                    Please log in to leave a comment
                  </p>
                  <Link to="/login">
                    <Button variant="outline">Log In to Comment</Button>
                  </Link>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Comments List */}
          <div className="space-y-4">
            {comments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <MessageCircle className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p>No comments yet. Be the first to share your thoughts!</p>
              </div>
            ) : (
              comments.map((comment) => (
                <Card key={comment.id} className="border-border/50" data-testid={`comment-${comment.id}`}>
                  <CardContent className="p-4 sm:p-6">
                    <div className="flex gap-3">
                      <div className="w-10 h-10 rounded-full bg-muted flex items-center justify-center flex-shrink-0">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="font-medium text-foreground">
                            {comment.user_name || 'Anonymous'}
                          </span>
                          <span className="text-xs text-muted-foreground">
                            {formatCommentDate(comment.created_at)}
                          </span>
                          {comment.is_edited && (
                            <span className="text-xs text-muted-foreground">(edited)</span>
                          )}
                        </div>
                        
                        {editingComment === comment.id ? (
                          <div>
                            <Textarea
                              value={editContent}
                              onChange={(e) => setEditContent(e.target.value)}
                              rows={3}
                              className="mb-2"
                              maxLength={2000}
                            />
                            <div className="flex gap-2">
                              <Button 
                                size="sm" 
                                onClick={() => handleEditComment(comment.id)}
                                className="btn-accent"
                              >
                                Save
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => { setEditingComment(null); setEditContent(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <p className="text-foreground whitespace-pre-wrap break-words">
                            {comment.content}
                          </p>
                        )}

                        {/* Comment Actions */}
                        {currentUser && (currentUser.id === comment.user_id || currentUser.role === 'admin') && editingComment !== comment.id && (
                          <div className="flex gap-2 mt-3">
                            {currentUser.id === comment.user_id && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs"
                                onClick={() => { setEditingComment(comment.id); setEditContent(comment.content); }}
                              >
                                <Pencil className="h-3 w-3 mr-1" />
                                Edit
                              </Button>
                            )}
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 px-2 text-xs text-destructive hover:text-destructive"
                              onClick={() => handleDeleteComment(comment.id)}
                            >
                              <Trash2 className="h-3 w-3 mr-1" />
                              Delete
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="mt-16">
            <h2 className="font-serif text-2xl font-bold text-foreground mb-8">
              Related Articles
            </h2>
            <div className="grid sm:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link 
                  key={relatedPost.id} 
                  to={`/blog/${relatedPost.slug || relatedPost.id}`}
                  className="group"
                >
                  <Card className="h-full overflow-hidden hover:shadow-lg transition-all border-border/50">
                    <div className="aspect-[16/10] overflow-hidden bg-muted">
                      {relatedPost.cover_image ? (
                        <img
                          src={relatedPost.cover_image}
                          alt={relatedPost.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-accent/10">
                          <BookOpen className="h-8 w-8 text-primary/30" />
                        </div>
                      )}
                    </div>
                    <CardContent className="p-4">
                      <h3 className="font-serif font-semibold text-foreground line-clamp-2 group-hover:text-primary transition-colors">
                        {relatedPost.title}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-2">
                        {formatDate(relatedPost.published_at)}
                      </p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </section>
        )}
      </article>
    </div>
  );
};

export default BlogPost;
